import React, { useEffect, useRef, useState } from 'react';
import { billingAPI } from '../services/api';
import PlanCelebration from './PlanCelebration';
import styles from '../styles/CheckoutReturn.module.css';

/**
 * Handles the return from a real Stripe Checkout (?checkout=success|cancelled).
 *
 * Activation is webhook-driven, so right after paying the subscription may not
 * exist locally yet. Flow:
 *   - cancelled → silently clean the URL, render nothing.
 *   - success   → calm full-screen "Payment received — setting up your
 *                 Sophia…" while polling /api/billing/me; when the entitlement
 *                 lands, a FULL CELEBRATION (same video + confetti language as
 *                 TodoCompletionModal's "Mission Complete") — this is the
 *                 moment someone becomes a paying member, it should feel like
 *                 one. CTA reloads with a clean URL so every screen sees the
 *                 new entitlements.
 *   - slow (>30s) → honest reassurance; no need to pay again.
 *
 * Self-contained: render it anywhere; it does nothing without the param.
 */
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;

function CheckoutReturn() {
  const params = new URLSearchParams(window.location.search);
  const outcome = params.get('checkout');
  const sessionId = params.get('session_id');
  const [state, setState] = useState(outcome === 'success' ? 'waiting' : 'idle'); // waiting | active | slow | idle
  const [hasTogether, setHasTogether] = useState(false);
  const startedAt = useRef(Date.now());
  const synced = useRef(false);

  // Cancelled (or no param): just make sure the URL is clean.
  useEffect(() => {
    if (outcome === 'cancelled') {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [outcome]);

  // Activate: first PULL the session straight from Stripe via the server
  // (works with no webhook — localhost, fresh deploys), then keep polling
  // /api/billing/me as the webhook-driven fallback.
  useEffect(() => {
    if (state !== 'waiting') return undefined;

    let stopped = false;
    let timer;
    const tick = async () => {
      try {
        if (sessionId && !synced.current) {
          synced.current = true; // one shot — the poll below covers retries
          const r = await billingAPI.syncCheckoutSession(sessionId).catch(() => null);
          const subs = r?.subscriptions || {};
          if (!stopped && (subs.base || subs.addon)) {
            setHasTogether(!!subs.addon);
            setState('active');
            return;
          }
        }
        const billing = await billingAPI.getBilling();
        const subs = billing?.subscriptions || {};
        if (!stopped && (subs.base || subs.addon)) {
          setHasTogether(!!subs.addon);
          setState('active');
          return;
        }
      } catch (e) { /* keep polling */ }
      if (stopped) return;
      if (Date.now() - startedAt.current > POLL_TIMEOUT_MS) {
        setState('slow');
        return;
      }
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();
    return () => { stopped = true; clearTimeout(timer); };
  }, [state, sessionId]);

  if (outcome !== 'success' || state === 'idle') return null;

  // Full reload with a clean URL so every screen (access gating, family
  // plan, menus) re-reads the new entitlements.
  const handleDone = () => window.location.replace('/');

  if (state === 'active') {
    return <PlanCelebration hasTogether={hasTogether} onDone={handleDone} />;
  }

  // waiting / slow — calm, dark, full-screen (the quiet before the celebration)
  return (
    <div className={styles.screen}>
      <div className={styles.inner}>
        <div className={styles.eyebrow}>Sophia</div>
        <h1 className={styles.title}>Payment received</h1>
        {state === 'waiting' ? (
          <>
            <p className={styles.text}>Setting up your Sophia…</p>
            <div className={styles.dots}>
              <span className={styles.dot} />
              <span className={styles.dot} />
              <span className={styles.dot} />
            </div>
          </>
        ) : (
          <>
            <p className={styles.text}>
              Activation is taking a little longer than usual — it will appear
              automatically in a few minutes. You don&apos;t need to pay again.
            </p>
            <button className={styles.button} onClick={handleDone}>
              Continue to Sophia
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default CheckoutReturn;
